import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'

export interface NotificationFieldMeta {
  path: string
  nameAr: string
  nameEn: string
  type: string
}

export interface NotificationEventDescriptor {
  eventKey: string
  module: string
  entityName: string
  triggerType: string
  nameAr: string
  nameEn: string
  actionUrlTemplate: string
  fields: NotificationFieldMeta[]
}

export interface NotificationCondition {
  id?: string
  parentId?: string | null
  sortOrder: number
  logic: 'AND' | 'OR'
  isGroup: boolean
  fieldPath?: string
  operator?: string
  value?: string
  valueTo?: string
  children: NotificationCondition[]
}

export interface NotificationRecipient {
  id?: string
  recipientType: string
  userId?: string
  roleId?: number
  fieldPath?: string
  sortOrder: number
}

export interface NotificationChannel {
  id?: string
  channelKey: string
  isEnabled: boolean
  subjectTemplate?: string
  bodyTemplate?: string
}

export interface NotificationWorkflowStep {
  id?: string
  stepOrder: number
  stepType: string
  waitMinutes?: number
  escalateRecipientType?: string
  escalateUserId?: string
  checkField?: string
  checkOperator?: string
  checkValue?: string
}

export interface NotificationDefinition {
  id?: string
  code: string
  name: string
  description?: string
  eventKey: string
  isActive: boolean
  priority: string
  scheduleType: string
  delayMinutes?: number
  sendAtTime?: string
  relativeField?: string
  relativeOffsetMinutes?: number
  recurrenceType?: string
  recurrenceTime?: string
  conditions: NotificationCondition[]
  recipients: NotificationRecipient[]
  channels: NotificationChannel[]
  workflow: NotificationWorkflowStep[]
}

export interface NotificationInboxItem {
  id: string
  title: string
  message: string
  entityName?: string
  entityId?: string
  actionUrl?: string
  priority: string
  isRead: boolean
  isArchived: boolean
  isHidden?: boolean
  isDeleted?: boolean
  createdAt: string
}

export interface NotificationQueueItem {
  id: string
  definitionId?: string
  definitionName?: string
  channelKey: string
  recipientType?: string
  recipientUserId?: string
  recipientName?: string
  recipientAddress?: string
  subject?: string
  body?: string
  payloadJson?: string
  entityName?: string
  entityId?: string
  actionUrl?: string
  status: string
  attempts: number
  scheduledAt: string
  sentAt?: string
  lastError?: string
  isEscalation: boolean
  createdAt: string
}

export interface NotificationCatalog {
  modules: string[]
  events: NotificationEventDescriptor[]
  operators: string[]
  recipientTypes: string[]
  channels: string[]
  scheduleTypes: string[]
}

const E = API_ENDPOINTS.notifications.engine

export const notificationEngineService = {
  catalog: async () => (await apiClient.get<NotificationCatalog>(E.catalog)).data,
  list: async () => (await apiClient.get<NotificationDefinition[]>(E.definitions)).data,
  get: async (id: string) => (await apiClient.get<NotificationDefinition>(E.definition(id))).data,
  save: async (dto: NotificationDefinition) => (await apiClient.post<NotificationDefinition>(E.definitions, dto)).data,
  remove: async (id: string) => apiClient.delete(E.definition(id)),
  setActive: async (id: string, isActive: boolean) => apiClient.post(E.definitionActive(id), { isActive }),
  test: async (id: string) => apiClient.post(E.definitionTest(id)),
  providers: async () => (await apiClient.get(E.providers)).data,
  saveProvider: async (dto: { channelKey: string; isEnabled: boolean; settingsJson?: string }) =>
    apiClient.post(E.providers, dto),
  users: async () => (await apiClient.get<{ userId: string; name: string; email?: string; phone?: string }[]>(E.users)).data,
  roles: async () => (await apiClient.get<{ roleId: number; name: string }[]>(E.roles)).data,
  inbox: async (unreadOnly = false, take = 50, includeHidden = false) =>
    (await apiClient.get<{ data: NotificationInboxItem[]; unreadCount: number }>(E.inbox, { params: { unreadOnly, take, includeHidden } })).data,
  markRead: async (id: string) => apiClient.post(E.inboxRead(id)),
  markAllRead: async () => apiClient.post(E.inboxReadAll),
  archive: async (id: string) => apiClient.post(E.inboxArchive(id)),
  monitor: async (status?: string, channel?: string) =>
    (await apiClient.get<NotificationQueueItem[]>(E.monitor, { params: { status, channel, take: 120 } })).data,
  retry: async (id: string) => apiClient.post(E.monitorRetry(id)),
  cancel: async (id: string) => apiClient.post(E.monitorCancel(id)),
}

export function emptyDefinition(): NotificationDefinition {
  return {
    code: '',
    name: '',
    eventKey: 'Receipt.Created',
    isActive: true,
    priority: 'normal',
    scheduleType: 'Immediate',
    conditions: [],
    recipients: [{ recipientType: 'CreatedBy', sortOrder: 0 }],
    channels: [
      {
        channelKey: 'InApp',
        isEnabled: true,
        subjectTemplate: 'سند جديد {{Number}}',
        bodyTemplate: 'تم إنشاء {{Number}}\nالمبلغ: {{Amount}}\nالعميل: {{Customer.Name}}\nالفرع: {{Branch.Name}}',
      },
    ],
    workflow: [],
  }
}
